/**
 * PIP body — fully inline-styled so it renders correctly inside the
 * documentPictureInPicture window even if stylesheet copy fails.
 * Mirrors legacy AttendanceRegularisation/PipTimerWindow layout.
 */

import type { SessionRow } from "@/api/hooks/timetracking";

function fmt(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function PipTimerContent({
  session,
  activeMs,
  pausedMs,
  onPause,
  onResume,
  onSwitch,
  onStop,
  busy,
}: {
  session: SessionRow;
  activeMs: number;
  pausedMs: number;
  onPause: () => void;
  onResume: () => void;
  onSwitch: () => void;
  onStop: () => void;
  busy?: boolean;
}) {
  const isWorking = session.state === "working";

  return (
    <div
      style={{
        width: "100%",
        // No fixed 100% height — content stacks naturally so no white gap
        // appears between the task description and the action buttons when
        // the PiP window is taller than the rendered content.
        display: "flex",
        flexDirection: "column",
        background: "#ffffff",
        color: "#1e293b",
        fontFamily:
          '"Inter", "Segoe UI", Roboto, system-ui, -apple-system, Arial, sans-serif',
        userSelect: "none",
        overflow: "hidden",
      }}
    >
      {/* Header: ctrl_no + status dot */}
      <div
        style={{
          padding: "8px 12px",
          background: isWorking ? "#f0fdf4" : "#fef3c7",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <span
          style={{
            fontWeight: 600,
            fontSize: 12,
            color: isWorking ? "#059669" : "#b45309",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "85%",
          }}
        >
          {session.workUnitCtrlNo ?? "—"}
        </span>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: isWorking ? "#059669" : "#d97706",
            boxShadow: isWorking ? "0 0 8px #059669" : "none",
          }}
        />
      </div>

      {/* Timers — Active + Paused both visible */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          padding: "12px",
          background: "#f9fafb",
          gap: 10,
        }}
      >
        <TimerRow
          label="Active"
          value={fmt(activeMs)}
          color={isWorking ? "#059669" : "#374151"}
          dot={isWorking ? "#059669" : "#9ca3af"}
          live={isWorking}
        />
        <TimerRow
          label="Paused"
          value={fmt(pausedMs)}
          color={!isWorking ? "#d97706" : "#374151"}
          dot={!isWorking ? "#d97706" : "#9ca3af"}
          live={!isWorking}
        />

        <div
          style={{
            alignSelf: "center",
            marginTop: 2,
            color: isWorking ? "#059669" : "#b45309",
            textTransform: "uppercase",
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: 1,
            background: isWorking ? "#d1fae5" : "#fef3c7",
            padding: "2px 10px",
            borderRadius: 4,
          }}
        >
          {isWorking
            ? "● Working"
            : `⏸ Paused${session.pauseReason ? ` · ${session.pauseReason.replace(/_/g, " ")}` : ""}`}
        </div>
      </div>

      {/* Context — Project / WorkUnit / Client on three clear rows */}
      <div
        style={{
          padding: "8px 12px",
          background: "#f3f4f6",
          borderTop: "1px solid #e5e7eb",
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <ContextRow label="Project" value={session.workUnitProjectName ?? "—"} />
        <ContextRow label="Client" value={session.workUnitClientName ?? "—"} />
      </div>

      {/* Controls */}
      <div
        style={{
          padding: "8px",
          background: "#f9fafb",
          borderTop: "1px solid #e5e7eb",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <div style={{ display: "flex", gap: 6 }}>
          {isWorking ? (
            <PipBtn onClick={onPause} disabled={busy} bg="#fef3c7" fg="#b45309">
              ⏸ Pause
            </PipBtn>
          ) : (
            <PipBtn onClick={onResume} disabled={busy} bg="#d1fae5" fg="#059669">
              ▶ Resume
            </PipBtn>
          )}
          <PipBtn onClick={onSwitch} disabled={busy} bg="#dbeafe" fg="#1d4ed8">
            ⇄ Switch
          </PipBtn>
        </div>
        <PipBtn onClick={onStop} disabled={busy} bg="#fee2e2" fg="#b91c1c">
          ■ Stop
        </PipBtn>
      </div>
    </div>
  );
}

function ContextRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 8,
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontSize: 9,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: "#9ca3af",
          width: 60,
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 11,
          color: "#374151",
          fontWeight: 600,
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={value}
      >
        {value}
      </span>
    </div>
  );
}

function TimerRow({
  label,
  value,
  color,
  dot,
  live,
}: {
  label: string;
  value: string;
  color: string;
  dot: string;
  live: boolean;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: dot,
            animation: live ? "blink 1s infinite" : "none",
          }}
        />
        <span
          style={{
            fontSize: 10,
            color: "#6b7280",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {label}
        </span>
      </div>
      <span
        style={{
          fontSize: 20,
          fontWeight: 700,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontVariantNumeric: "tabular-nums",
          letterSpacing: 0.5,
          color,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function PipBtn({
  onClick,
  disabled,
  bg,
  fg,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  bg: string;
  fg: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: 1,
        padding: "8px 12px",
        border: "none",
        borderRadius: 6,
        background: bg,
        color: fg,
        fontWeight: 600,
        fontSize: 11,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "filter 0.15s ease",
      }}
    >
      {children}
    </button>
  );
}
